/**
 * QuestionAnimationPlayer
 *
 * Renders a CBR animation video clip for questions that have an animationId.
 * - Autoplays muted + looped (short 4-5s clips, CBR situational format)
 * - Preloads eagerly for zero perceived delay
 * - Fully responsive across all screen sizes
 * - Renders nothing when videoUrl is null (graceful — videos not yet hosted)
 */

import React, { memo, useRef, useEffect, useState } from 'react';
import { ANIMATION_PLAYBACK_MAP } from '../../question_data/animations/animationPlaybackRegistry';

// Track URLs already fetched into cache so we never double-fetch
const _prefetched = new Set<string>();

/**
 * Warms the browser HTTP cache for one animation video via fetch().
 * fetch() fills the HTTP cache properly across all browsers; the video
 * element then loads instantly from cache with no network round-trip.
 */
export const preloadAnimationVideo = (animationId: string | null | undefined): void => {
  if (!animationId) return;
  const anim = ANIMATION_PLAYBACK_MAP.get(animationId);
  if (!anim?.videoUrl || _prefetched.has(anim.videoUrl)) return;
  _prefetched.add(anim.videoUrl);
  fetch(anim.videoUrl, { mode: 'cors', cache: 'force-cache' }).catch(() => {});
};

/**
 * Batch-preloads all unique animation videos for a set of questions.
 * Call this once when a test's questions are loaded so every video is
 * cached before the user reaches it.
 */
export const preloadAnimationsForQuestions = (
  questions: Array<{ animationId?: string | null }>,
  options?: {
    maxImmediate?: number;
    staggerMs?: number;
  }
): void => {
  const maxImmediate = Math.max(1, options?.maxImmediate ?? 2);
  const staggerMs = Math.max(0, options?.staggerMs ?? 160);
  const seen = new Set<string>();
  let index = 0;
  for (const q of questions) {
    if (q.animationId && !seen.has(q.animationId)) {
      seen.add(q.animationId);
      const id = q.animationId;
      if (index < maxImmediate) {
        preloadAnimationVideo(id);
      } else {
        setTimeout(() => preloadAnimationVideo(id), (index - maxImmediate + 1) * staggerMs);
      }
      index += 1;
    }
  }
};

interface QuestionAnimationPlayerProps {
  animationId: string | null | undefined;
  onVideoError?: () => void;
}

export const QuestionAnimationPlayer = memo<QuestionAnimationPlayerProps>(({ animationId, onVideoError }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [videoReady, setVideoReady] = useState(false);
  const [videoError, setVideoError] = useState(false);
  const animation = animationId ? ANIMATION_PLAYBACK_MAP.get(animationId) : undefined;

  // Restart video and reset ready state whenever the question changes
  useEffect(() => {
    setVideoReady(false);
    setVideoError(false);
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = 0;
    v.play().catch(() => {/* autoplay blocked — canPlay will still fire */});
  }, [animationId]);

  if (!animation?.videoUrl || videoError) return null;

  return (
    <div className={`practice-animation-container${videoReady ? '' : ' practice-animation-loading'}`}>
      <video
        ref={videoRef}
        className="practice-animation-video"
        src={animation.videoUrl}
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        aria-label={animation.sceneDescription}
        style={videoReady ? undefined : { opacity: 0 }}
        onCanPlay={() => setVideoReady(true)}
        onError={() => { setVideoError(true); onVideoError?.(); }}
      />
    </div>
  );
});

QuestionAnimationPlayer.displayName = 'QuestionAnimationPlayer';
